<template>
  <component
    :is="as"
    class="AiTypo"
    :class="{
      [`AiTypo--${variant}`]: Boolean(variant),
      [`AiTypo--color-${color}`]: Boolean(color),
      'AiTypo--underline': underline,
    }">
    <slot />
  </component>
</template>

<script lang="ts" setup>
import type { AiTypoVariant, TypoColor } from './types';

export interface AiTypoProps {
  as: keyof HTMLElementTagNameMap;
  color?: TypoColor;
  variant?: AiTypoVariant;
  underline?: boolean;
}

withDefaults(defineProps<AiTypoProps>(), {
  color: 'default',
  variant: 'heading-01',
});
</script>
<style lang="scss">
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/fonts';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/themes/rixos/constants' as RixosTheme;

.AiTypo {
  color: colors.$primary;
  white-space: pre-line;

  &--underline {
    text-decoration: underline;
  }

  &--heading-01 {
    color: colors.$heading-primary;
    @include fonts.heading-01;
  }

  &--heading-02 {
    color: colors.$heading-primary;
    @include fonts.heading-02;
  }

  &--heading-03 {
    color: colors.$heading-primary;
    @include fonts.heading-03;
  }

  &--heading-04 {
    color: colors.$heading-primary;
    @include fonts.heading-04;
  }

  &--paragraph-01-regular {
    @include fonts.paragraph-01-regular;
  }

  &--paragraph-01-bold {
    @include fonts.paragraph-01-bold;
  }

  &--paragraph-02-regular {
    @include fonts.paragraph-02-regular;
  }

  &--paragraph-02-bold {
    @include fonts.paragraph-02-bold;
  }

  &--paragraph-03-regular {
    @include fonts.paragraph-03-regular;
  }

  &--paragraph-03-bold {
    @include fonts.paragraph-03-bold;
  }

  &--paragraph-03-italic {
    @include fonts.paragraph-03-italic;
  }

  &--paragraph-04-regular {
    @include fonts.paragraph-04-regular;
  }

  &--paragraph-04-bold {
    @include fonts.paragraph-04-bold;
  }

  &--button-regular {
    @include fonts.button-regular;
  }

  &--button-regular-underline {
    @include fonts.button-regular-underline;
  }

  &--button-bold {
    @include fonts.button-bold;
  }

  &--button-bold-underline {
    @include fonts.button-bold-underline;
  }

  &--navigation-link {
    @include fonts.navigation-link;
  }

  &--tab-link-01 {
    @include fonts.tab-link-01;
  }

  &--tab-link-02 {
    @include fonts.tab-link-02;
  }

  &--featured {
    @include fonts.featured;
  }

  &--overline-01 {
    @include fonts.overline-01;
  }

  &--overline-02 {
    @include fonts.overline-02;
  }

  &--legal-regular {
    @include fonts.legal-regular;
  }
  &--legal-bold {
    @include fonts.legal-bold;
  }
  &--icon-text {
    @include fonts.icon-text;
  }

  &--color {
    &-white {
      color: colors.$white;
    }
    &-inherit {
      color: inherit;
    }
    &-gold,
    &-gold-900 {
      color: colors.$gold-900;
    }
    &-gold-400 {
      color: colors.$gold-400;
    }
    &-gold-300 {
      color: colors.$gold-300;
    }
    &-stratos-300 {
      color: colors.$stratos-300;
    }
    &-stratos-500 {
      color: colors.$stratos-500;
    }
    &-stratos-700 {
      color: colors.$stratos-700;
    }
    &-stratos-800 {
      color: colors.$stratos-800;
    }
    &-stratos-900 {
      color: colors.$stratos-900;
    }
    &-neutral-100 {
      color: colors.$neutral-100;
    }
    &-neutral-300 {
      color: colors.$neutral-300;
    }
    &-neutral-400 {
      color: colors.$neutral-400;
    }
    &-neutral-800 {
      color: colors.$neutral-800;
    }
    &-success {
      color: colors.$success;
    }
    &-red-700 {
      color: colors.$red-700;
    }
    &-red-600 {
      color: colors.$red-600;
    }
    &-red-400 {
      color: colors.$red-400;
    }
    &-green-500 {
      color: colors.$green-500;
    }
    &-green-300 {
      color: colors.$green-300;
    }
    &-error {
      color: colors.$error;
    }
  }
}

.theme.rixos {
  .AiTypo {
    color: RixosTheme.$color-primary-black;

    &--heading-01 {
      @include fonts.rixos-theme--text-h1;
    }

    &--heading-02 {
      @include fonts.rixos-theme--text-h2;
    }

    &--heading-03 {
      @include fonts.rixos-theme--text-h3;
    }

    &--heading-04 {
      @include fonts.rixos-theme--text-h4;
    }

    &--paragraph-01-regular {
      @include fonts.rixos-theme--paragraph-01-regular;
    }

    &--paragraph-01-bold {
      @include fonts.rixos-theme--paragraph-01-bold;
    }

    &--paragraph-02-regular {
      @include fonts.rixos-theme--paragraph-02-regular;
    }

    &--paragraph-02-bold {
      @include fonts.rixos-theme--paragraph-02-bold;
    }

    &--paragraph-03-regular {
      @include fonts.rixos-theme--paragraph-03-regular;
    }

    &--paragraph-03-bold {
      @include fonts.rixos-theme--paragraph-03-bold;
    }

    &--paragraph-03-italic {
      @include fonts.rixos-theme--paragraph-03-italic;
    }

    &--paragraph-04-regular {
      @include fonts.rixos-theme--paragraph-04-regular;
    }

    &--paragraph-04-bold {
      @include fonts.rixos-theme--paragraph-04-bold;
    }

    &--button-regular {
      @include fonts.rixos-theme--button-regular;
    }

    &--button-regular-underline {
      @include fonts.rixos-theme--button-regular-underline;
    }

    &--button-bold {
      @include fonts.rixos-theme--button-bold;
    }

    &--button-bold-underline {
      @include fonts.rixos-theme--button-bold-underline;
    }

    &--navigation-link {
      @include fonts.rixos-theme--navigation-link;
    }

    &--tab-link-01 {
      @include fonts.rixos-theme--tab-link-01;
    }

    &--tab-link-02 {
      @include fonts.rixos-theme--tab-link-02;
    }

    &--featured {
      @include fonts.rixos-theme--featured;
    }

    &--overline-01 {
      @include fonts.rixos-theme--overline-01;
    }

    &--overline-02 {
      @include fonts.rixos-theme--overline-02;
    }

    &--legal-regular {
      @include fonts.rixos-theme--legal-regular;
    }

    &--legal-bold {
      @include fonts.rixos-theme--legal-bold;
    }

    &--icon-text {
      @include fonts.rixos-theme--icon-text;
    }
  }
}
</style>
